import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaMoon, FaSun } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import MatrixBackground from './components/MatrixBackground';
import LanguageSwitcher from './components/LanguageSwitcher';
import UserMenu from './components/UserMenu';
import HomePage from './pages/HomePage';
import AssignmentsPage from './pages/AssignmentsPage';
import UsefulPage from './pages/UsefulPage';
import MessagesPage from './pages/MessagesPage';
import AdminPage from './pages/AdminPage';
import { useTheme } from './context/ThemeContext';

function Navigation() {
  const { isDarkMode, toggleTheme } = useTheme();
  const { t } = useTranslation();
  const location = useLocation();

  const smoothAnimation = {
    hidden: {
      opacity: 0,
      y: 20,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.6, -0.05, 0.01, 0.99],
      },
    },
  };

  const scrollToSection = (sectionId) => {
    if (location.pathname !== '/') {
      window.location.href = '/#' + sectionId;
      return;
    }
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  return (
    <motion.nav
      initial="hidden"
      animate="visible"
      variants={smoothAnimation}
      className={`${isDarkMode ? 'bg-dark-overlay/80' : 'bg-white/80'} backdrop-blur-md shadow-lg sticky top-0 z-50`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16 items-center">
          <Link to="/" className={`text-xl font-bold ${isDarkMode ? 'text-primary hover:text-secondary' : 'text-green-600 hover:text-green-700'} transition-colors duration-300`}>
            Doston Usmonov
          </Link>
          <div className="flex items-center gap-4 md:gap-8">
            <motion.a
              onClick={() => scrollToSection('about')}
              className={`nav-link ${isDarkMode ? 'text-light hover:text-primary' : 'text-gray-700 hover:text-green-600'} transition-colors duration-300 text-lg cursor-pointer hidden md:block`}
            >
              {t('nav.about')}
            </motion.a>
            <motion.a
              onClick={() => scrollToSection('skills')}
              className={`nav-link ${isDarkMode ? 'text-light hover:text-primary' : 'text-gray-700 hover:text-green-600'} transition-colors duration-300 text-lg cursor-pointer hidden md:block`}
            >
              {t('nav.skills')}
            </motion.a>
            <motion.a
              onClick={() => scrollToSection('contact')}
              className={`nav-link ${isDarkMode ? 'text-light hover:text-primary' : 'text-gray-700 hover:text-green-600'} transition-colors duration-300 text-lg cursor-pointer hidden md:block`}
            >
              {t('nav.contact')}
            </motion.a>
            {/* A route rather than a scroll target, so it stays visible on mobile too. */}
            <Link
              to="/foydali"
              className={`nav-link transition-colors duration-300 text-lg ${location.pathname === '/foydali'
                ? isDarkMode ? 'text-primary' : 'text-green-600'
                : isDarkMode ? 'text-light hover:text-primary' : 'text-gray-700 hover:text-green-600'
                }`}
            >
              {t('nav.useful')}
            </Link>
            <LanguageSwitcher />
            <UserMenu />
            <button
              onClick={toggleTheme}
              className={`p-2 rounded-full ${isDarkMode
                ? 'bg-gray-800 text-yellow-400 hover:bg-gray-700'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                } transition-all duration-200`}
              aria-label={t('nav.toggleTheme')}
            >
              {isDarkMode ? <FaSun className="w-5 h-5" /> : <FaMoon className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>
    </motion.nav>
  );
}

function Footer() {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();

  const smoothAnimation = {
    hidden: {
      opacity: 0,
      y: 20,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.6, -0.05, 0.01, 0.99],
      },
    },
  };

  return (
    <motion.footer
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      variants={smoothAnimation}
      className={`${isDarkMode ? 'bg-dark-overlay/80' : 'bg-white/80'} backdrop-blur-md ${isDarkMode ? 'text-light' : 'text-gray-700'} py-8 mt-16`}
    >
      <div className="section-container text-center">
        <p>{t('footer.rights', { year: new Date().getFullYear() })}</p>
      </div>
    </motion.footer>
  );
}

function App() {
  const { isDarkMode } = useTheme();

  return (
    <Router>
      <div className={`min-h-screen relative ${isDarkMode ? 'bg-[#0a192f]' : 'bg-gray-50'}`}>
        <MatrixBackground />

        <Navigation />

        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/assignments" element={<AssignmentsPage />} />
          <Route path="/foydali" element={<UsefulPage />} />
          <Route path="/xabarlar" element={<MessagesPage />} />
          <Route path="/admin" element={<AdminPage />} />
        </Routes>

        <Footer />
      </div>
    </Router>
  );
}

export default App;
